import Button from 'components/Button';
import FormGroup from 'components/FormGroup';
import {
  TypeGooglePlaceDetail,
  GooglePlaceAutoComplete,
} from 'components/GooglePlaceAutoComplete';
import MapPreview from 'components/MapPreview';
import Modal from 'components/Modal';
import SelectField from 'components/SelectField';
import TextField from 'components/TextField';
import Typography from 'components/Typography';
import {useState} from 'react';
import {useForm, Controller} from 'react-hook-form';
import toast from 'react-hot-toast';
import {Address, createAddress} from 'types';
import {labels} from '../UserAddresses/UserAddresses';
import useUserForm from '../useUserForm';
import styles from './CreateAddressModal.module.scss';

type CreateAddressModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  customer_id: number | undefined;
  onSuccess: (data: Address) => void;
  newAddressType?: createAddress['type'];
  title?: string;
};

function CreateAddressModal({
  isOpen,
  handleClose,
  onSuccess,
  customer_id = undefined,
  newAddressType = 'dispatch',
  title = 'Add New Address',
}: CreateAddressModalProps) {
  const [loading, setLoading] = useState(false);
  const {control, handleSubmit, reset, setValue} = useForm<createAddress>();

  const [location, setLocation] = useState({
    latitude: 40.7596958,
    longitude: -73.9650045,
  });

  const onSelectPlace = (placeDetail: TypeGooglePlaceDetail) => {
    setValue('zipcode', placeDetail.zipcode);
    setValue('address_line_1', placeDetail.addressLine1);
    setValue('city', placeDetail.city);
    setValue('state', placeDetail.state);
    setValue('latitude', placeDetail.lat);
    setValue('longitude', placeDetail.lon);
    setLocation({latitude: placeDetail.lat, longitude: placeDetail.lon});
  };

  const {createNewAddress} = useUserForm();

  const handleCreation = async (data: createAddress) => {
    setLoading(true);
    try {
      const res = await createNewAddress({
        ...data,
        // address type is dispatch by default
        type: newAddressType,
        customer_id,
      });
      handleClose();
      onSuccess(res.data);
      toast.success('Address created successfully');

      reset();
      setLoading(false);
    } catch (error) {
      toast.error('Something wrong with creation. Try again later !');
      setLoading(false);
    }
  };

  const isBilling = newAddressType === 'billing';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      className={styles.list_wrapper}
      overlayClassName={styles.overlay}
    >
      <Typography variant="h2">
        {title || `Add New ${isBilling && 'Billing'} Address`}
      </Typography>
      <div className={styles.modalWrapper}>
        <FormGroup>
          <Controller
            name="city"
            control={control}
            rules={{required: 'Please, Select the address'}}
            render={({fieldState}) => (
              <GooglePlaceAutoComplete
                label="Your address"
                placeholder="Address"
                onPlaceSelected={onSelectPlace}
                error={fieldState.error?.message}
                options={{
                  types: ['address'],
                }}
              />
            )}
          />
          <Controller
            name="unit_address"
            control={control}
            render={({field, fieldState}) => (
              <TextField
                label="Unit number"
                wrapperClass={styles.googleInput}
                placeholder="Enter unit number"
                hasError={false}
                onChange={field.onChange}
                error={fieldState.error?.message}
              />
            )}
          />
        </FormGroup>

        <FormGroup>
          <Controller
            name="label"
            control={control}
            rules={{required: 'Please select label of address'}}
            render={({field, fieldState}) => (
              <SelectField
                {...field}
                label="Address Label"
                placeholder="Select a label"
                options={labels}
                error={fieldState.error?.message}
                value={labels.find((item) => item.value === field.value)}
                onChange={(newValue) => {
                  if (newValue) {
                    setValue('label', newValue.value as 'home');
                  }
                }}
              />
            )}
          />
        </FormGroup>
        <div className={styles.actionWrapper}>
          <Button
            loading={loading}
            type="submit"
            onClick={handleSubmit(handleCreation)}
          >
            Save Address
          </Button>
        </div>
        <div
          style={{
            paddingTop: 20,
          }}
        >
          {!!location.latitude && (
            <MapPreview
              zoom={15}
              onDrag={(location) => {
                setValue('latitude', location.lat);
                setValue('longitude', location.lon);
              }}
              center={[location.latitude, location.longitude]}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

export default CreateAddressModal;
